/* eslint-disable @typescript-eslint/no-explicit-any */
import { AxiosRequestConfig } from "axios";
import { apiClient } from "./client";

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    lastPage: number;
    perPage: number;
    total: number;
  };
}

// Server pagination format (Django REST framework style)
export interface ServerPaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export type ApiError = {
  message: string;
  errors?: Record<string, string[]>;
  status: number;
};

export interface BaseEntity {
  id: string | number;
  [key: string]: any;
}

export interface QueryParams {
  page?: number;
  perPage?: number;
  sort?: string;
  order?: "asc" | "desc";
  [key: string]: any;
}

export interface ServiceOptions {
  enableGetAll?: boolean;
  enableGetById?: boolean;
  enableCreate?: boolean;
  enableUpdate?: boolean;
  enableDelete?: boolean;
}

// Default options with all methods enabled
const defaultOptions: ServiceOptions = {
  enableGetAll: true,
  enableGetById: true,
  enableCreate: true,
  enableUpdate: true,
  enableDelete: true,
};

export class BaseService<
  T extends BaseEntity,
  CreateDTO = Omit<T, "id">,
  UpdateDTO = Partial<CreateDTO>
> {
  private options: ServiceOptions;

  constructor(
    private readonly endpoint: string,
    options: ServiceOptions = defaultOptions
  ) {
    // Merge provided options with defaults
    this.options = { ...defaultOptions, ...options };
  }

  getAll = async (
    params: QueryParams = {},
    config?: AxiosRequestConfig
  ): Promise<T[]> => {
    if (!this.options.enableGetAll) {
      throw new Error("Method 'getAll' is not enabled for this service");
    }

    const response = await apiClient.get<T[]>(config?.url ?? this.endpoint, {
      params,
      ...config,
    });
    return response.data;
  };

  getById = async (id: string, config?: AxiosRequestConfig): Promise<T> => {
    if (!this.options.enableGetById) {
      throw new Error("Method 'getById' is not enabled for this service");
    }

    const response = await apiClient.get<T>(
      `${config?.url ?? this.endpoint}${id}/`,
      config
    );
    return response.data;
  };

  create = async (data: CreateDTO, config?: AxiosRequestConfig): Promise<T> => {
    if (!this.options.enableCreate) {
      throw new Error("Method 'create' is not enabled for this service");
    }

    const response = await apiClient.post<T>(
      config?.url ?? this.endpoint,
      data,
      config
    );
    return response.data;
  };

  update = async (
    id: string,
    data: UpdateDTO,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    if (!this.options.enableUpdate) {
      throw new Error("Method 'update' is not enabled for this service");
    }

    const response = await apiClient.patch<T>(
      `${config?.url ?? this.endpoint}${id}/`,
      data,
      config
    );
    return response.data;
  };

  delete = async (id: string, config?: AxiosRequestConfig): Promise<void> => {
    if (!this.options.enableDelete) {
      throw new Error("Method 'delete' is not enabled for this service");
    }

    await apiClient.delete(`${config?.url ?? this.endpoint}${id}/`, config);
  };
}
